import {
  SLIDE_FADE_MS,
  SLIDE_SETTLE_MS,
  STAGE_WIDTH,
  SWIPE_FLICK_VELOCITY,
  SWIPE_THRESHOLD,
  TAP_SLOP_PX,
} from '../config/kiosk'
import { Welcome } from './welcome'
import type { Slide } from '../core/slides'

/**
 * Full-screen slideshow of the pictures editors publish on the church website.
 *
 * - Turns on its own every slide's duration, with a crossfade.
 * - A swipe left or right turns it by hand; the picture follows the finger.
 * - While a finger is down nothing turns on its own; on release the slide's full
 *   duration starts again.
 * - A new list from the website waits for the next turn, so it never interrupts a
 *   swipe or a fade. With 0–1 slides there are no turns, so it applies at once.
 *
 * Only three <img> layers exist (previous, current, next). They swap roles when the
 * slide turns, so a turn never reloads a picture and memory stays flat on old
 * WebViews.
 */

type State = 'idle' | 'fading' | 'dragging' | 'settling'

export class Slideshow {
  private slides: Slide[] = []
  private index = 0
  private state: State = 'idle'
  private timer: number | undefined
  private pending: Slide[] | null = null
  private showing = false

  private prev: HTMLImageElement
  private cur: HTMLImageElement
  private next: HTMLImageElement
  private readonly empty: HTMLElement
  private readonly welcome = new Welcome()

  private pointerId: number | null = null
  private startX = 0
  private dx = 0
  private lastX = 0
  private lastT = 0
  private velocity = 0

  constructor(private readonly root: HTMLElement) {
    root.classList.add('slideshow')
    this.prev = this.layer()
    this.cur = this.layer()
    this.next = this.layer()

    this.empty = this.welcome.el
    this.welcome.start()
    root.appendChild(this.empty)

    root.addEventListener('pointerdown', this.onDown)
    root.addEventListener('pointermove', this.onMove)
    root.addEventListener('pointerup', this.onUp)
    root.addEventListener('pointercancel', this.onCancel)
  }

  /** The slides on screen, without any whose picture failed to load. */
  displayed(): Slide[] {
    return this.slides.slice()
  }

  /** True while a finger is down or a swipe is still sliding into place. */
  isInteracting(): boolean {
    return this.state === 'dragging' || this.state === 'settling'
  }

  /** Hands over a new list. It is shown at the next turn (or at once with nothing to turn). */
  setSlides(slides: Slide[]): void {
    this.pending = slides
    if (this.state === 'idle' && this.timer === undefined) this.applyPending(false)
  }

  /* ---------------------------------------------------------------- layers */

  private layer(): HTMLImageElement {
    const img = document.createElement('img')
    img.className = 'slideshow__layer'
    img.alt = ''
    img.draggable = false
    // Through CORS, so the service worker may keep the picture (vite.config.ts).
    img.crossOrigin = 'anonymous'
    img.addEventListener('error', () => this.onImageError(img))
    this.root.appendChild(img)
    return img
  }

  private setSrc(img: HTMLImageElement, url: string): void {
    if (img.dataset.url === url) return
    img.dataset.url = url
    img.src = url
  }

  private place(img: HTMLImageElement, x: number, opacity: number, z: number, transition = 'none'): void {
    img.style.transition = transition
    img.style.transform = x === 0 ? '' : `translateX(${x}px)`
    img.style.opacity = String(opacity)
    img.style.zIndex = String(z)
    // A layer nobody can see is not drawn, so at rest only one picture uses GPU memory.
    img.style.visibility = opacity > 0 || transition !== 'none' ? 'visible' : 'hidden'
  }

  /** Current slide shown, neighbors loaded but hidden. */
  private rest(): void {
    const n = this.slides.length
    if (n === 0) {
      this.showEmpty()
      return
    }
    this.place(this.cur, 0, 1, 1)
    this.place(this.prev, 0, 0, 0)
    this.place(this.next, 0, 0, 0)
    if (n >= 2) {
      const prev = this.slides[(this.index - 1 + n) % n]
      const next = this.slides[(this.index + 1) % n]
      if (prev) this.setSrc(this.prev, prev.url)
      if (next) this.setSrc(this.next, next.url)
    }
  }

  /** Makes `incoming` the current layer; the other two become the neighbors. */
  private promote(incoming: HTMLImageElement): void {
    const others = [this.prev, this.cur, this.next].filter((img) => img !== incoming)
    this.cur = incoming
    this.prev = others[0] as HTMLImageElement
    this.next = others[1] as HTMLImageElement
  }

  /* ------------------------------------------------------------- the list */

  private applyPending(advance: boolean): void {
    const list = this.pending
    if (!list) return
    this.pending = null

    const currentId = this.showing ? this.slides[this.index]?.id : undefined
    const oldIndex = this.index
    this.slides = list.slice()

    const n = this.slides.length
    if (n === 0) {
      this.showEmpty()
      return
    }

    let target: number
    const found = currentId === undefined ? -1 : this.slides.map((s) => s.id).indexOf(currentId)
    if (found >= 0) target = advance ? (found + 1) % n : found
    else target = Math.min(oldIndex, n - 1)

    this.goTo(target)
  }

  private showEmpty(): void {
    this.clearTimer()
    this.state = 'idle'
    this.root.classList.remove('is-moving')
    this.showing = false
    this.index = 0
    this.empty.style.display = ''
    this.welcome.start()
    for (const img of [this.prev, this.cur, this.next]) this.place(img, 0, 0, 0)
  }

  /** Shows slide `target`: crossfades if a different picture is on screen, else just settles. */
  private goTo(target: number): void {
    const slide = this.slides[target]
    if (!slide) return
    this.empty.style.display = 'none'
    this.welcome.stop()

    if (!this.showing) {
      this.index = target
      this.setSrc(this.cur, slide.url)
      this.showing = true
      this.rest()
      this.afterTurn()
      return
    }
    if (this.cur.dataset.url === slide.url) {
      this.index = target
      this.rest()
      this.afterTurn()
      return
    }
    this.fadeTo(target)
  }

  private fadeTo(target: number): void {
    const slide = this.slides[target]
    if (!slide) return
    const incoming = this.next.dataset.url === slide.url ? this.next : this.prev.dataset.url === slide.url ? this.prev : this.next
    this.setSrc(incoming, slide.url)
    this.state = 'fading'

    void whenLoaded(incoming).then(() => {
      if (incoming.dataset.url !== slide.url) {
        // The picture failed and onImageError took it out of the list.
        this.state = 'idle'
        this.rest()
        this.afterTurn()
        return
      }
      this.place(incoming, 0, 0, 2)
      void incoming.offsetWidth // commit the start of the fade
      this.place(incoming, 0, 1, 2, `opacity ${SLIDE_FADE_MS}ms ease`)
      window.setTimeout(() => {
        this.promote(incoming)
        this.index = target
        this.state = 'idle'
        this.rest()
        this.afterTurn()
      }, SLIDE_FADE_MS)
    })
  }

  /** After any turn: apply a waiting list when nothing will turn, else start the clock. */
  private afterTurn(): void {
    if (this.pending && this.slides.length < 2) {
      this.applyPending(false)
      return
    }
    this.scheduleTimer()
  }

  /* ---------------------------------------------------------------- timer */

  private scheduleTimer(): void {
    this.clearTimer()
    const slide = this.slides[this.index]
    if (this.state !== 'idle' || this.slides.length < 2 || !slide) return
    this.timer = window.setTimeout(this.onTimer, slide.durationMs)
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      window.clearTimeout(this.timer)
      this.timer = undefined
    }
  }

  private readonly onTimer = (): void => {
    this.timer = undefined
    if (this.state !== 'idle') return
    if (this.pending) this.applyPending(true)
    else this.fadeTo((this.index + 1) % this.slides.length)
  }

  /* ---------------------------------------------------------------- swipe */

  private readonly onDown = (event: PointerEvent): void => {
    if (this.pointerId !== null || this.state !== 'idle' || this.slides.length < 2) return
    this.pointerId = event.pointerId
    this.state = 'dragging'
    this.clearTimer()
    try {
      this.root.setPointerCapture(event.pointerId)
    } catch {
      // Capture is a nicety; the swipe still works within the panel.
    }
    // Promote the layers to the GPU only while a finger moves them: on a weak
    // board repainting full-size pictures every move would stutter, and keeping
    // them promoted all the time ran a phone out of GPU memory.
    this.root.classList.add('is-moving')
    this.startX = this.lastX = event.clientX
    this.lastT = event.timeStamp
    this.dx = 0
    this.velocity = 0
    this.drag()
  }

  private readonly onMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return
    const scale = stageScale()
    this.dx = (event.clientX - this.startX) / scale
    const dt = event.timeStamp - this.lastT
    if (dt > 0) this.velocity = (event.clientX - this.lastX) / scale / dt
    this.lastX = event.clientX
    this.lastT = event.timeStamp
    this.drag()
  }

  private readonly onUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return
    this.pointerId = null
    const far = Math.abs(this.dx) > STAGE_WIDTH * SWIPE_THRESHOLD
    const flick = Math.abs(this.dx) > TAP_SLOP_PX && Math.abs(this.velocity) > SWIPE_FLICK_VELOCITY
    if (far || flick) this.settle(this.dx < 0 ? 1 : -1)
    else this.settle(0)
  }

  private readonly onCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return
    this.pointerId = null
    this.settle(0)
  }

  private drag(): void {
    this.place(this.cur, this.dx, 1, 1)
    this.place(this.prev, this.dx - STAGE_WIDTH, 1, 1)
    this.place(this.next, this.dx + STAGE_WIDTH, 1, 1)
  }

  /** Slides into place: 1 = next slide, -1 = previous, 0 = back to the current one. */
  private settle(direction: 1 | -1 | 0): void {
    this.state = 'settling'
    const t = `transform ${SLIDE_SETTLE_MS}ms ease-out`
    const shift = -direction * STAGE_WIDTH
    this.place(this.cur, shift, 1, 1, t)
    this.place(this.prev, shift - STAGE_WIDTH, 1, 1, t)
    this.place(this.next, shift + STAGE_WIDTH, 1, 1, t)

    window.setTimeout(() => {
      const n = this.slides.length
      if (direction === 1) {
        this.promote(this.next)
        this.index = (this.index + 1) % n
      } else if (direction === -1) {
        this.promote(this.prev)
        this.index = (this.index - 1 + n) % n
      }
      this.state = 'idle'
      this.root.classList.remove('is-moving')
      this.rest()
      this.afterTurn()
    }, SLIDE_SETTLE_MS)
  }

  /* --------------------------------------------------------------- errors */

  /** A picture that cannot load (e.g. offline and no longer cached) leaves the rotation. */
  private onImageError(img: HTMLImageElement): void {
    const url = img.dataset.url
    img.removeAttribute('src')
    delete img.dataset.url
    const at = this.slides.map((s) => s.url).indexOf(url ?? '')
    if (at === -1) return

    const wasCurrent = at === this.index
    this.slides.splice(at, 1)
    console.warn('[slideshow] picture failed, skipped:', url)

    if (this.slides.length === 0) {
      this.showEmpty()
      return
    }
    if (at < this.index) this.index -= 1
    this.index %= this.slides.length
    if (this.state !== 'idle') return // the turn in progress finishes with rest()

    if (wasCurrent) {
      const slide = this.slides[this.index]
      if (slide) this.setSrc(this.cur, slide.url)
    }
    this.rest()
    this.scheduleTimer()
  }
}

function stageScale(): number {
  return Number(document.documentElement.style.getPropertyValue('--stage-scale')) || 1
}

/**
 * Resolves when the picture is loaded and, where the browser can say so, decoded —
 * a hidden layer may not be decoded yet, and the fade would start on a blank
 * picture. Gives up after 3 s regardless. (decode() is Chrome 64+; older WebViews
 * skip that step.)
 */
function whenLoaded(img: HTMLImageElement): Promise<void> {
  return loaded(img).then(() => {
    if (typeof img.decode !== 'function' || !(img.naturalWidth > 0)) return
    return withLimit(img.decode().catch(() => undefined), 3_000)
  })
}

function withLimit(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms)
    void promise.then(() => {
      window.clearTimeout(timer)
      resolve()
    })
  })
}

function loaded(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve()
  return new Promise((resolve) => {
    const done = (): void => {
      img.removeEventListener('load', done)
      img.removeEventListener('error', done)
      window.clearTimeout(timer)
      resolve()
    }
    const timer = window.setTimeout(done, 3_000)
    img.addEventListener('load', done)
    img.addEventListener('error', done)
  })
}
